/**
 * workosConvexAuth.ts — bridge WorkOS AuthKit → Convex.
 *
 * `ConvexProviderWithAuth` (convex/react) expects a `useAuth` hook returning
 * { isLoading, isAuthenticated, fetchAccessToken }. WorkOS AuthKit's
 * `useAuth()` (from @workos-inc/authkit-react) exposes { isLoading, user,
 * getAccessToken }, so this adapter maps one onto the other. It must be used
 * inside an <AuthKitProvider>.
 */

import { useCallback, useMemo } from "react";
import { useAuth } from "@workos-inc/authkit-react";

export function useWorkOSConvexAuth(): {
  isLoading: boolean;
  isAuthenticated: boolean;
  fetchAccessToken: (args: {
    forceRefreshToken: boolean;
  }) => Promise<string | null>;
} {
  const { isLoading, user, getAccessToken } = useAuth();

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      try {
        const token = await getAccessToken({ forceRefresh: forceRefreshToken });
        return token ?? null;
      } catch {
        // No active session / refresh failed — treat as unauthenticated.
        return null;
      }
    },
    [getAccessToken],
  );

  return useMemo(
    () => ({
      isLoading,
      isAuthenticated: user !== null,
      fetchAccessToken,
    }),
    [isLoading, user, fetchAccessToken],
  );
}
